import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Calendar, ArrowRight } from 'lucide-react';

const inputCls = 'rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0 text-[16px]';
const labelCls = 'uppercase tracking-luxe text-[11px] font-semibold text-ink-muted mb-2 block';

export default function ConsultationModal({ open, onOpenChange, categoryId, draftCustomization }) {
  const { isAuthenticated, user } = useAuth();
  const [name, setName] = useState(isAuthenticated ? user?.name || '' : '');
  const [mobile, setMobile] = useState(isAuthenticated ? user?.phone || '' : '');
  const [date, setDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const timeSlots = ['10:00 – 12:00', '12:00 – 14:00', '14:00 – 16:00', '16:00 – 18:00'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/consultation-requests', {
        name, mobile, category_id: categoryId,
        preferred_date: date, preferred_time_slot: timeSlot, notes,
        draft_customization: draftCustomization,
      });
      setSubmitted(true);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const handleClose = () => { setSubmitted(false); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg rounded-none border-border bg-background p-8" data-testid="consultation-modal" aria-describedby="consultation-desc">
        {submitted ? (
          <div className="py-6 text-center space-y-4" data-testid="consultation-success">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-gold text-gold mx-auto">
              <Check className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="eyebrow">Confirmed</div>
            <div className="font-display text-ink" style={{ fontSize: 28, lineHeight: 1.15 }}>Consultation booked.</div>
            <p id="consultation-desc" className="text-ink/70" style={{ fontSize: 14, lineHeight: 1.6 }}>We'll confirm your appointment shortly via WhatsApp.</p>
            <button onClick={handleClose} className="mt-6 inline-flex items-center justify-center gap-2 border border-ink text-ink hover:bg-ink hover:text-background uppercase tracking-luxe font-medium px-8 py-3 transition-all duration-300" style={{ fontSize: 12 }}>
              Close
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="text-left">
              <div className="eyebrow mb-3">By Appointment</div>
              <DialogTitle className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 32 }}>
                Book a Consultation
              </DialogTitle>
              <p id="consultation-desc" className="text-ink/70 pt-2" style={{ fontSize: 14 }}>Our stylist will visit you for a personalised session.</p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 pt-4" data-testid="consultation-form">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="cons-name" className={labelCls}>Name</Label>
                  <Input id="cons-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="consultation-name-input" className={inputCls} placeholder="Your name" />
                </div>
                <div>
                  <Label htmlFor="cons-mobile" className={labelCls}>Mobile</Label>
                  <Input id="cons-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} required data-testid="consultation-mobile-input" className={inputCls} placeholder="+91 …" />
                </div>
              </div>
              <div>
                <Label htmlFor="cons-date" className={labelCls}>Preferred Date</Label>
                <Input id="cons-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required data-testid="consultation-date-input" className={inputCls} />
              </div>
              <div>
                <Label className={labelCls}>Preferred Time Slot</Label>
                <div className="grid grid-cols-2 gap-2.5 mt-2">
                  {timeSlots.map((ts) => (
                    <button
                      key={ts}
                      type="button"
                      onClick={() => setTimeSlot(ts)}
                      className={`px-3 py-3 uppercase tracking-luxe text-[11px] font-semibold border transition-all ${
                        timeSlot === ts ? 'bg-ink text-background border-ink' : 'border-border text-ink hover:border-ink'
                      }`}
                      data-testid={`time-slot-${ts.replace(/\s/g, '')}`}
                    >
                      {ts}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="cons-notes" className={labelCls}>Notes (optional)</Label>
                <Input id="cons-notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="consultation-notes-input" className={inputCls} placeholder="Anything we should know?" />
              </div>
              <button
                type="submit"
                disabled={loading || !date || !timeSlot}
                data-testid="consultation-submit-btn"
                className="w-full inline-flex items-center justify-center gap-3 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium py-4 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-ink disabled:hover:text-background"
                style={{ fontSize: 13 }}
              >
                <Calendar className="w-4 h-4" />{loading ? 'Booking…' : (<>Book Consultation <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
